import React, { useMemo } from 'react';

import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';

import { messages } from './messages';
import { FulfillmentPlanFulfillmentType } from '../../../enums/fulfillment-plan-fulfillment-type';
import { EligibleToEditStatuses } from '../../../enums/fulfillmentplan-order-grid-status';
import { stopClickPropagation } from '../../../services/event-handler-helpers';
import Global from '../../../store/global-selectors';
import { useSortedStrings } from '../../../hooks/use-sorted-translations';
import { useSaveShipFrom } from '../hooks/use-save-ship-from';
import { setupExternalShipFromIdForContext } from '../../../fulfillment-planner';

const fpStatusCanEditShipfrom = new Set(EligibleToEditStatuses);

const sortOptions = {
  labelProp: 'name',
};

const ShipFromColumn = ({ fulfillmentPlanId, isDisabled }) => {
  const shipFromId = useSelector((state) => {
    const fp = Global.selectEntitiesFulfillmentPlanById(state, {
      fulfillmentPlanId,
    });
    const _shipFromId = fp?.shipFromId;
    if (_shipFromId) {
      return _shipFromId;
    }
    return null;
  });
  const externalShipFromId = useSelector((state) => {
    const fp = Global.selectEntitiesFulfillmentPlanById(state, {
      fulfillmentPlanId,
    });
    const unformattedExternalShipFromId =
      fp?.sellerFulfillmentProviderId || fp?.externalFulfillmentConfiguration?.sellerFulfillmentProviderId;
    if (unformattedExternalShipFromId) {
      return setupExternalShipFromIdForContext(unformattedExternalShipFromId);
    }
    return null;
  });

  const orderGridStatus = useSelector((state) =>
    Global.selectEntitiesFulfillmentPlanOrderGridStatus(state, {
      fulfillmentPlanId,
    }),
  );
  const fulfillmentType = useSelector((state) =>
    Global.selectEntitiesFulfillmentPlanFulfillmentType(state, {
      fulfillmentPlanId,
    }),
  );
  const sellerFulfillmentProviders = useSelector(Global.selectSellerAllFulfillmentProviders);

  const externalShipFrom = sellerFulfillmentProviders.find((sfp) => sfp.shipFromId === externalShipFromId);

  const labelShipFrom = useSelector((state) => Global.selectSellerShipFromsById(state, { shipFromId }));
  const shipFrom = labelShipFrom ?? externalShipFrom;

  const fulfillmentProviders = useSelector(Global.selectSellerActiveFulfillmentProviders);
  const shipFroms = useSelector(Global.selectSellerShipFroms);
  const shipFromsForDropdown = useMemo(() => {
    const fulfillmentShipFroms = fulfillmentProviders.map((provider) => ({
      name: provider.name,
      id: provider.shipFromId,
    }));
    return shipFroms.map(({ shipFromId: id, name }) => ({ id, name })).concat(fulfillmentShipFroms);
  }, [fulfillmentProviders, shipFroms]);

  const shipFromsForDropdownSorted = useSortedStrings(shipFromsForDropdown, sortOptions);

  const selectedShipFrom = useMemo(
    () => shipFromsForDropdownSorted.find(({ id }) => id === shipFromId || id === externalShipFromId) || {},
    [shipFromsForDropdownSorted, shipFromId, externalShipFromId],
  );

  const saveShipFrom = useSaveShipFrom(fulfillmentPlanId, selectedShipFrom.id);

  const shouldRenderDropdown = useMemo(() => shipFroms.length <= 100, [shipFroms]);

  if (shouldRenderDropdown && !isDisabled && fpStatusCanEditShipfrom.has(orderGridStatus)) {
    return (
      <span onClick={stopClickPropagation}>
        <DropdownMenu stopScrollPropagation>
          <DropdownMenuToggler stopClickPropagation theme='link'>
            {selectedShipFrom.name || <FormattedMessage {...messages.columnDropdownNoneSelected} />}
          </DropdownMenuToggler>
          {shipFromsForDropdownSorted.map(({ id, name }) => (
            <DropdownMenuItem key={id} name={id} onClick={saveShipFrom}>
              {name}
            </DropdownMenuItem>
          ))}
        </DropdownMenu>
      </span>
    );
  }
  if (fulfillmentType === FulfillmentPlanFulfillmentType.Manual) {
    return <FormattedMessage defaultMessage='External' description='Mark as Shipped Ship From Column' id='8UGz5z' />;
  }
  return shipFrom?.name ?? '';
};

ShipFromColumn.propTypes = {
  fulfillmentPlanId: PropTypes.string.isRequired,
  isDisabled: PropTypes.bool.isRequired,
};

export const ShipFrom = {
  getMessageDescriptor: () => messages.shipFrom,
  id: 'shipFrom',

  render: (data, { isExpanded = false, isFirstRow = true, isDisabled = false } = {}) => {
    if (isExpanded && !isFirstRow) {
      return null;
    }
    return <ShipFromColumn fulfillmentPlanId={data.id} isDisabled={isDisabled} />;
  },
};
